/**
 * SampleSectionClearPrompt — "you just made a real one; shall I clear the samples?" (VRG-512)
 *
 * ── THE PRODUCT ASK, AND THE ONE RULE IT TURNS ON ────────────────────────────────────
 *
 * Sample data is offered on Home and cleared from Home. That is fine while a brand is
 * still exploring, and wrong at the moment they stop: someone who has just created their
 * first real certificate is on the CERTIFICATES page, looking at their record sitting in
 * a list beside six fabricated ones, and the control that resolves it is a different
 * page away. So each surface asks for itself, at the moment the question becomes live.
 *
 * ⚠️ THE PROMPT NEVER GATES THE CREATE. It fires AFTER the record is saved, and Cancel
 * leaves everything exactly as it was. A housekeeping question in front of a user's real
 * work is a worse trade than the untidy list it would tidy — and a modal that appeared
 * BEFORE the save would additionally be asking about a state that did not exist yet.
 *
 * ── "ASKED ALREADY" IS RECORDED BY THE DATA, NOT BY A FLAG ───────────────────────────
 *
 * The obvious implementations are a `localStorage` key or a new `companies` column, and
 * both are worse than the thing that is already true:
 *
 *   - `localStorage` is per BROWSER. Sample data is per TENANT, so a colleague on another
 *     machine — or the same person on a laptop — gets asked about data someone else has
 *     already decided about. The key would be answering for the wrong subject.
 *   - A column (`intro_prompts_dismissed`) is a migration, and then a second one the first
 *     time a section is added. `companies.intro_cleared_at` is the precedent for tenant
 *     state of this kind, and it earns its keep because nothing else can express "this
 *     tenant chose to stop" — that is not the case here.
 *
 * The condition is a TRANSITION, not a state: fire when the tenant went from having NO
 * real certificates to having one. That can happen once, so the prompt can appear once,
 * with nothing persisted. `useIntroData().hasRealData` is exactly that bit — the API
 * derives it from `certificates WHERE example_kind IS NULL` — so the hook reads it before
 * the create and compares after.
 *
 * The honest edge: delete your only real certificate and make another, and you will be
 * asked again. That is the transition genuinely happening a second time, and being asked
 * again in a tenant that still has samples is the correct answer rather than a bug.
 *
 * ── ONE SURFACE, TWO STEPS — NOT A SECOND MODAL ──────────────────────────────────────
 *
 * The secondary path ("clear everything, not just this page") swaps this modal's own body
 * and confirm label rather than opening another dialog over it. §52's argument, one layer
 * up: a confirm stacked on a confirm has two Cancels on screen and no way to tell which
 * unwinds what. The user still confirms the wider action explicitly — the step swap
 * changes what the button says and what the warning claims, so nothing is smuggled.
 */
import { useCallback, useRef, useState } from 'react'

import { Box, Link, Stack, Typography } from '@mui/material'

import { Alert } from '@repo/ui'

import { ConfirmSettingModal } from 'components/feedback/modals/ConfirmSettingModal/ConfirmSettingModal'
import { useCanAccess } from 'hooks'
import { useClearIntroData, useClearIntroSection, useIntroData } from 'hooks/api/useIntroData'
import type { ClearableIntroSection } from 'services'

/** Which step the one surface is showing. */
type PromptScope = 'section' | 'all'

interface SectionCopy {
  /** Plural noun, lower case, as it appears mid-sentence. */
  nouns: string
  /** What else is removed BECAUSE it cannot outlive the records being removed. */
  dependants: string
  /** The sample things that survive, named so "this page only" is checkable. */
  survivors: string
}

/**
 * Per-section copy, mirroring the server's `intro-sections.ts` registry.
 *
 * ⚠️ `dependants` is not decoration and must never be left empty to make a sentence
 * shorter. Clearing the sample certificates also removes their sample scans and transfer
 * history — not as a side effect, but because a scan is a fact ABOUT a certificate and
 * cannot outlive it (`transfers.certificate_id` is ON DELETE CASCADE). A consequence that
 * said only "the sample certificates" would be true about the row count and misleading
 * about the analytics the user is about to see change.
 *
 * Only sections the server will actually scope appear here. `owners`, `products` and
 * `orders` are absent on purpose — see `ClearableIntroSection` and the server registry
 * for the foreign keys that make scoping them dishonest today.
 */
const SECTION_COPY: Record<ClearableIntroSection, SectionCopy> = {
  certificates: {
    nouns: 'certificates',
    dependants: 'their sample scans and transfer history',
    survivors: 'sample products, people, tags and orders',
  },
}

export interface SampleSectionClearPromptProps {
  section: ClearableIntroSection
  open: boolean
  onClose: () => void
}

/**
 * The modal. Controlled — `useSampleSectionClearPrompt` below owns when it opens.
 */
export const SampleSectionClearPrompt = ({
  section,
  open,
  onClose,
}: SampleSectionClearPromptProps) => {
  const copy = SECTION_COPY[section]
  const [scope, setScope] = useState<PromptScope>('section')

  const { clearIntroSection, isClearingSection, clearSectionError } = useClearIntroSection()
  const { clearIntroData, isClearing, clearError } = useClearIntroData()

  const loading = isClearingSection || isClearing
  const error = scope === 'section' ? clearSectionError : clearError

  const handleClose = useCallback(() => {
    if (loading) return
    onClose()
    // Reset to the narrow step for the next time this mounts, so a user who widened it
    // once is not silently re-offered the wide action as the default.
    setScope('section')
  }, [loading, onClose])

  const handleConfirm = useCallback(() => {
    const onDone = {
      onSuccess: () => {
        onClose()
        setScope('section')
      },
    }
    // Left OPEN on failure, deliberately, matching `ClearSampleDataButton`: closing on
    // error would leave the samples in place with nothing on screen saying why.
    if (scope === 'section') clearIntroSection(section, onDone)
    else clearIntroData(undefined, onDone)
  }, [clearIntroData, clearIntroSection, onClose, scope, section])

  return (
    <ConfirmSettingModal
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      loading={loading}
      destructive
      title={
        scope === 'section'
          ? `Clear the sample ${copy.nouns}?`
          : 'Clear all of your sample data?'
      }
      /**
       * ⚠️ THE PREAMBLE ENDS ON THE QUESTION, and the question is on its own line.
       *
       * Product direction: the description should close by ASKING, rather than describing
       * the situation and leaving the reader to infer that the footer button is the answer.
       * The two halves then read as one exchange — the paragraph explains what is true, the
       * last line asks what to do about it, and the confirm button answers in the same verb
       * ("clear"), so the question and its answer cannot drift apart.
       *
       * Rendered as a block SPAN inside the preamble's existing `Typography` rather than as
       * a second paragraph, so it inherits the §15 `lineHeight: 1.5` and the secondary
       * colour that `ConfirmSettingModal` already sets once. A sibling `Typography` would
       * be a second place those decisions have to be kept in step.
       *
       * Scope-aware, like every other string here: the section step names its own nouns
       * (`copy.nouns`) so it stays correct when a second section is registered, and the
       * `all` step asks about the whole set.
       */
      preamble={
        <>
          {scope === 'section'
            ? `Your first real certificate is saved. Your console still has the sample ` +
              `${copy.nouns} it was set up with, and they are mixed into this list.`
            : 'This removes the whole sample set, not only the records on this page.'}
          {/*
            NO `mt` HERE, deliberately. `display: 'block'` already puts the question on
            its own line, and the gap the reader sees is the parent's line-height leading
            (§15's 1.5 on ~16px prose, so ~8px), which is exactly the gap between any two
            wrapped lines above it.

            An `mt: 2` was added here first and read as wrong on sight: it STACKS 8px of
            margin on top of that leading, so the question sat roughly twice as far from
            the sentence above it as that sentence's own wrapped lines sit from each
            other. It looked like a new paragraph when it is the closing line of the same
            one. §40's warning applies literally — the visual gap is not the CSS value you
            set, because line-height contributes leading the margin then adds to.
          */}
          <Box component="span" sx={{ display: 'block' }}>
            {scope === 'section'
              ? `Do you want to clear the sample ${copy.nouns}?`
              : 'Do you want to clear all of your sample data?'}
          </Box>
        </>
      }
      consequence={
        scope === 'section'
          ? `The sample ${copy.nouns} will be deleted, along with ${copy.dependants}. ` +
            `Your ${copy.survivors} stay. Nothing you created is affected.`
          : 'Every sample record will be deleted across the whole console. Nothing you ' +
            'created is affected.'
      }
      confirmLabel={scope === 'section' ? `Clear sample ${copy.nouns}` : 'Clear all sample data'}
    >
      <Stack spacing={3}>
        {error && (
          <Alert
            severity="error"
            description={error.message || 'That did not work. Please try again.'}
          />
        )}

        {/*
          The secondary path. A text Link rather than a second Button: two buttons of
          equal weight in a confirm's body compete with the real confirm in the footer,
          and this is the rarer choice of the two.
        */}
        <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
          {scope === 'section' ? (
            <>
              Finished exploring everywhere?{' '}
              <Link
                component="button"
                type="button"
                underline="always"
                disabled={loading}
                onClick={() => setScope('all')}
                sx={{ verticalAlign: 'baseline', font: 'inherit' }}
              >
                Clear all sample data instead
              </Link>
              .
            </>
          ) : (
            <>
              Only want this page tidied?{' '}
              <Link
                component="button"
                type="button"
                underline="always"
                disabled={loading}
                onClick={() => setScope('section')}
                sx={{ verticalAlign: 'baseline', font: 'inherit' }}
              >
                Just clear the sample {copy.nouns}
              </Link>
              .
            </>
          )}
        </Typography>
      </Stack>
    </ConfirmSettingModal>
  )
}

/**
 * Wire a surface up to the prompt. Call `notifyRealRecordCreated()` after a create
 * SUCCEEDS, and spread `promptProps` onto `<SampleSectionClearPrompt />`.
 *
 * ```tsx
 * const samplePrompt = useSampleSectionClearPrompt('certificates')
 * // ...in the create's onSuccess: samplePrompt.notifyRealRecordCreated()
 * <SampleSectionClearPrompt {...samplePrompt.promptProps} />
 * ```
 *
 * ── WHY THE "BEFORE" VALUE IS READ FROM A REF, NOT FROM THE RENDER ──────────────────
 *
 * `hasRealData` comes from a react-query cache that the create's own `onSuccess`
 * invalidates. So by the time a caller can plausibly call `notifyRealRecordCreated`, the
 * value in scope may ALREADY have been refetched as `true` — at which point "was it false
 * before?" is unanswerable and the prompt either never fires or fires every time,
 * depending on which way the race lands. The ref is written on every render, so it holds
 * the last rendered value, and the notify reads THAT rather than re-deriving.
 *
 * Gated on the permission: a prompt offering an action the user cannot perform is a
 * modal whose only working button is Cancel. This is the "hide" side of §27 — not an
 * in-context control to disable-with-a-tooltip, but an unsolicited interruption, and an
 * interruption you cannot act on is pure noise.
 */
export const useSampleSectionClearPrompt = (section: ClearableIntroSection) => {
  const { hasIntroData, hasRealData, isReady } = useIntroData()
  const { isHasAccess: canManageSamples } = useCanAccess({ action: 'sample_data_manage' })

  const [open, setOpen] = useState(false)

  const hadRealDataRef = useRef<boolean | undefined>(hasRealData)
  hadRealDataRef.current = hasRealData

  const notifyRealRecordCreated = useCallback(() => {
    if (!isReady) return
    if (!canManageSamples) return
    // Nothing to offer. Also the state after a successful clear, so the prompt cannot
    // reappear on the second real record.
    if (hasIntroData !== true) return
    // The transition is the trigger. `true` already means this is not their first real
    // record; `undefined` means we never knew, and guessing in either direction is worse
    // than staying quiet.
    if (hadRealDataRef.current !== false) return
    setOpen(true)
  }, [canManageSamples, hasIntroData, isReady])

  const onClose = useCallback(() => setOpen(false), [])

  return {
    notifyRealRecordCreated,
    promptProps: { section, open, onClose },
  }
}
